import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { catchError, map, Observable, of, Subject, take, takeUntil, tap } from 'rxjs';
import store from 'store2';
import { NewChatMessageLog } from '../../communication/communication.component';
import { ClinicPatientService } from 'src/app/services/clinic.patient.service';
import { CommonModule } from '@angular/common';
import { GetPatient } from 'src/app/models/get-patient';
import { SeachClinicPatientListModel } from 'src/app/models/clinicParms/search-clinic-patient-list-model';
import { MessageService } from '../../common/newMessage.service';
import { MenuParams } from 'src/app/models/ParamModels/PatientNotesDetailParam';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
	selector: 'app-sidebar',
	standalone: true,
	imports: [RouterModule, CommonModule],
	templateUrl: './sidebar.component.html',
	styleUrl: './sidebar.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();
	hidemenu: boolean = false;
	isWorldNetClient: boolean = store('paymentGateway') === 1;
	constructor(private _clinicPatientService: ClinicPatientService,
		private messageService: MessageService,
		private sanitizer: DomSanitizer,
		private cdr: ChangeDetectorRef) { }

	ngOnInit(): void {
		this.GetBranchWiseUserPermissions().pipe(take(1)).subscribe({
			next: () => {
				this.GetMenus();
				this.messageService.newMessageAvailable$.pipe(takeUntil(this.destroy$)).subscribe(val => {
					if (store('userBranches') == '182232' || store('userBranches') == 182232) {
						this.hidemenu = true;
					}
					this.newMessageAvailable = val;
					this.cdr.markForCheck();
				});
				this.loadPatients();
			},
			error: err => console.error('[Sidebar] Init failed', err)
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	newMessagesList: NewChatMessageLog[] = [];
	getUnreadMessages(): void {
		this._clinicPatientService.GetPatientUnreadMessagesCount().pipe(take(1)).subscribe(
			{
				next: (res) => {
					// this._toastr.showInfo("Unread messages fetched!");
					// console.log(res);
					if (res?.length > 0) {
						this.newMessagesList = res;
						this.msgAvailableFromAnyClinicUserCheck();
						// console.log(this.patients)
					}
					this.cdr.markForCheck();
				},
				error: (err) => {
					console.error(err);
				}
			}
		)
	}

	menulist: any[] = [];
	parentMenus: any[] = [];
	menuChildrenMap = new Map<number, any[]>();
	GetMenus(): void {
		let menuParams = new MenuParams();
		menuParams.userID = store('userID');
		// menuParams.roleID = 213;
		menuParams.branchID = store('userBranches');
		if (menuParams.branchID == null || menuParams.branchID == 0) {
			return;
		}
		this._clinicPatientService.GetMenus(menuParams).pipe(take(1)).subscribe(
			{
				next: (res) => {
					if (res?.length > 0) {
						this.menulist = res;
						this.sanitizeIcons();
						this.sanitizeIconsDesktop();
						this.buildMenuTree();
						this.cdr.markForCheck();
					}
				},
				error: (err) => {
					console.log(err);
				}
			}
		)
	}

	GetBranchWiseUserPermissions(): Observable<boolean> {
		let menuParams = new MenuParams();
		menuParams.userID = store('userID');
		menuParams.branchID = store('userBranches');

		return this._clinicPatientService
			.GetBranchWiseUserPermissions(menuParams)
			.pipe(
				take(1),
				tap((res) => {
					if (res?.length > 0) {
						store('userPermissions', JSON.stringify(res));
					}
				}),
				map(() => true),
				catchError(err => {
					console.error('Error fetching user permissions', err);
					return of(false);
				})
			);
	}


	buildMenuTree() {
		this.parentMenus = this.menulist
			.filter(m => m.parentId === null)
			.sort((a, b) => a.sortOrder - b.sortOrder);

		this.menulist
			.filter(m => m.parentId !== null)
			.forEach(child => {
				if (!this.menuChildrenMap.has(child.parentId!)) {
					this.menuChildrenMap.set(child.parentId!, []);
				}
				this.menuChildrenMap.get(child.parentId!)!.push(child);
			});

		// sort children
		this.menuChildrenMap.forEach(children =>
			children.sort((a, b) => a.sortOrder - b.sortOrder)
		);
	}

	hasChildren(menuId: number): boolean {
		return this.menuChildrenMap.has(menuId);
	}

	getChildren(menuId: number): any[] {
		return this.menuChildrenMap.get(menuId) || [];
	}

	sanitizeIconsDesktop() {
		this.menulist = this.menulist.map(menu => ({
			...menu,
			iconClass: menu.iconClass
				? this.sanitizer.bypassSecurityTrustHtml(menu.iconClass as string)
				: null
		}));
	}

	sanitizeIcons() {
		this.menulist = this.menulist.map(menu => {

			if (!menu.iconClass) {
				menu.safeIcon = '';
				return menu;
			}

			const uniqueId = 'icon_' + menu.menuIndex;

			let updated = menu.iconClass;

			//Replace all id="..."
			updated = updated.replace(/id="([^"]+)"/g, (match, idValue) => {
				return `id="${idValue}_${uniqueId}"`;
			});

			//Replace all url(#...)
			updated = updated.replace(/url\(#([^)]+)\)/g, (match, refValue) => {
				return `url(#${refValue}_${uniqueId})`;
			});

			//Replace href="#..."
			updated = updated.replace(/href="#([^"]+)"/g, (match, refValue) => {
				return `href="#${refValue}_${uniqueId}"`;
			});

			//Replace xlink:href="#..."
			updated = updated.replace(/xlink:href="#([^"]+)"/g, (match, refValue) => {
				return `xlink:href="#${refValue}_${uniqueId}"`;
			});

			menu.safeIcon = this.sanitizer.bypassSecurityTrustHtml(updated);

			return menu;
		});
	}

	patients: GetPatient[] = [];
	arePatientsLoading: boolean = false;
	loadPatients() {
		let demoSearchPatientListModel = new SeachClinicPatientListModel();
		demoSearchPatientListModel.branchID = store('userBranches');
		demoSearchPatientListModel.offset = 0;
		demoSearchPatientListModel.nextRows = 0;
		demoSearchPatientListModel.totalNum = 0;
		demoSearchPatientListModel.allowedBranches = store('userBranches');
		demoSearchPatientListModel.sortExpression = "Addon DESC";
		demoSearchPatientListModel.multiCampaign = -1;
		demoSearchPatientListModel.patientStatusID = "1";
		this.arePatientsLoading = true;
		this._clinicPatientService.GetPatients(demoSearchPatientListModel).pipe(takeUntil(this.destroy$)).subscribe(
			{
				next: res => {
					this.patients = res;
					this.cdr.markForCheck();
					this.getUnreadMessages();
					// console.log(res);
				},
				error: err => {
					console.error('Error loading patients:', err);
				}
			});
		//console.log(this.patientList());
		this.arePatientsLoading = false;
	}

	newMessageAvailable: boolean = false;
	msgAvailableFromAnyClinicUserCheck(): void {
		if (this.patients.length > 0 && this.newMessagesList != null) {
			this.newMessagesList.forEach(nm => {
				if (this.patients.filter(p => p.patientID == nm.patientID).length > 0) {
					// this.newMessageAvailable = true;					
					this.messageService.setNewMessageAvailable(true);
				}
			})
		}
	}

	currentUserRoleId = store('roleID');
	@HostBinding('class') hostClass = 'sidebar dark:bg-coal-600 bg-light border-r border-r-gray-200 dark:border-r-coal-100 fixed z-20 hidden lg:flex flex-col items-stretch shrink-0';
	@HostBinding('attr.data-drawer') drawer = 'true';
	@HostBinding('attr.data-drawer-class') drawerClass = 'drawer drawer-start top-0 bottom-0';
	@HostBinding('attr.data-drawer-enable') drawerEnable = 'true|lg:false';
	@HostBinding('attr.id') id = 'sidebar';

	trackByMenuId(index: number, menu: any): number { return menu.menuId; }
}
